import { useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';
import React from 'react';
import Button from '../misc/Button';

interface StepFlowProps {
  indexState: [number, React.Dispatch<React.SetStateAction<number>>];
  steps: React.ReactElement[];
  hideIndicators?: boolean;
  className?: string;
}

interface StepIndicatorProps {
  indexState: [number, React.Dispatch<React.SetStateAction<number>>];
  steps: React.ReactElement[];
  className?: string;
}

export function StepIndicator({ indexState: [index, setIndex], steps, className }: StepIndicatorProps): JSX.Element {
  return (
    <div className={twMerge('mx-auto flex h-2 w-full max-w-xl gap-6', className)}>
      {steps.map((s, i) => (
        <div
          key={i}
          onClick={index > i ? () => setIndex(i) : undefined}
          className={`h-[6px] w-full rounded bg-primary-900 transition-all ${i > index ? 'bg-opacity-15' : 'cursor-pointer'}`}
        />
      ))}
    </div>
  );
}

export default function StepFlow({
  indexState: [index, setIndex],
  steps,
  hideIndicators = false,
  className,
}: StepFlowProps): JSX.Element {
  return (
    <div className={twMerge('flex h-full w-full flex-1 flex-col items-center gap-6', className)}>
      {!hideIndicators && <StepIndicator indexState={[index, setIndex]} steps={steps} />}
      {steps[index]}
    </div>
  );
}

interface StepFlowStepProps {
  children: React.ReactNode;
  setStep: (i: 1 | -1) => void;
  showPrev?: boolean;
  showNext?: boolean;
  nextText?: string;
  onSubmit?: () => void;
  disabled?: boolean;
  submitText?: string;
  title?: string;
  className?: string;
}

export function StepFlowStep({
  children,
  setStep,
  onSubmit,
  disabled = false,
  showPrev = true,
  submitText,
  showNext = true,
  nextText,
  title,
  className,
}: StepFlowStepProps): JSX.Element {
  const { t } = useTranslation();
  return (
    <>
      <div className={twMerge('flex w-full max-w-xl flex-col gap-4', className)}>
        <div className="grid gap-2">
          {title && (
            <div className="mb-4 flex flex-col">
              <h1 className="font-semibold md:text-2xl">{title}</h1>
            </div>
          )}
          {children}
        </div>
        <div className="mb-6 ml-auto mt-auto flex gap-4">
          {showPrev && (
            <Button variant="secondary" onClick={() => setStep(-1)}>
              {t('general.back')}
            </Button>
          )}
          {showNext && (
            <Button variant="primary" disabled={disabled} onClick={() => setStep(1)}>
              {nextText ?? t('general.continue')}
            </Button>
          )}
          {!showNext && onSubmit && (
            <Button variant="primary" disabled={disabled} onClick={onSubmit}>
              {submitText ?? t('general.save')}
            </Button>
          )}
        </div>
      </div>
    </>
  );
}
